/**
 * Automatic offline preload.
 *
 * The band's data (rider, config, musicians, crew, stageplots, setlists,
 * checklists, itinerary, songbook, team, daysheet/pdf layouts — plus the shared
 * team/shows/venues/contacts/calendar) is fetched through the normal apiGet*
 * layer, which caches every successful GET into the IndexedDB offline cache
 * automatically (see src/api.ts realFetchJSON → cachePut). So "preloading" is
 * just firing those fetches in the background — the offline cache fills itself
 * and no manual button is needed anymore.
 *
 * `preloadShowBundle` then walks the upcoming shows and caches everything a
 * Day Sheet needs (roadmap, hotels, rooming, flights, contacts, distances,
 * guests, notes, riders, show checklists) so a musician can open any upcoming
 * show in airplane mode — first time, without ever having visited it online.
 *
 * Everything here is fire-and-forget: callers never await it, so it can never
 * slow down login or band load. Jobs run sequentially (as the old manual
 * preload page did) to avoid hammering the server, and each band is fetched at
 * most once per session.
 */
import {
  apiGetRider,
  apiGetConfig,
  apiGetMusicians,
  apiGetCrew,
  apiGetStagePlots,
  apiGetSetlists,
  apiGetChecklists,
  apiGetItinerary,
  apiGetTeam,
  apiGetShows,
  apiGetVenues,
  apiGetContacts,
  apiGetCalendar,
  apiGetSongs,
  apiGetShow,
  apiGetShowRoadmap,
  apiGetAccommodations,
  apiGetRooming,
  apiGetShowFlights,
  apiGetShowContacts,
  apiGetShowDistances,
  apiGetShowGuests,
  apiGetShowTechNotes,
  apiGetShowMiscNotes,
  apiGetShowCategorizedNotes,
  apiGetRiders,
  apiGetShowChecklists,
  apiGetDaysheetLayoutEffective,
  apiGetPdfLayoutEffective,
  todayMinDate,
  type ShowDTO,
} from '../api';

/** How many upcoming Day Sheets the automatic preload walks (kept light).
 *
 * 5, not 15 (2026-11-04): each Day Sheet costs ~13 GETs, so 15 shows meant ~195
 * requests on every app boot — with a global bundle + one full band bundle on top,
 * a single tab reload reached ~237 GETs, and a few reloads on Mac + phone sharing
 * one venue IP tripped the server's per-IP backstop (429 on /api/auth/me and
 * /api/auth/login). The manual `/preload` page still asks for 20 explicitly.
 */
export const AUTO_PRELOAD_UPCOMING_LIMIT = 5;

/**
 * Hard-refresh throttle (2026-11-04). The in-memory guards below (`globalDone`,
 * `doneBands`) only survive one page lifetime, so F5 / Cmd+R re-ran the whole
 * preload and re-downloaded resources that were already in IndexedDB 30 seconds
 * earlier. A per-tab sessionStorage stamp with a 10 min TTL fixes that without
 * making the cache stale for long: a new day's data still lands on the next visit.
 */
const PRELOAD_STAMP_TTL_MS = 10 * 60 * 1000;
const STAMP_GLOBAL = 'bandz.autopreload.global';
const stampBand = (bandId: string): string => `bandz.autopreload.band.${bandId}`;

/** Best-effort storage access: undefined in unit tests (node) and private mode. */
function preloadStorage(): Storage | null {
  try {
    return (globalThis as { sessionStorage?: Storage }).sessionStorage ?? null;
  } catch {
    return null; // storage access can throw when disabled by the browser
  }
}

/** True when the same tab preloaded this key less than PRELOAD_STAMP_TTL_MS ago. */
function recentlyPreloaded(key: string): boolean {
  try {
    const raw = preloadStorage()?.getItem(key);
    if (!raw) return false;
    const at = Number(raw);
    return Number.isFinite(at) && Date.now() - at < PRELOAD_STAMP_TTL_MS;
  } catch {
    return false; // unreadable stamp → never skip (be safe, preload)
  }
}

/** Record the stamp so a reload in the next 10 min does not redo the preload. */
function markPreloaded(key: string): void {
  try {
    preloadStorage()?.setItem(key, String(Date.now()));
  } catch {
    /* quota / private mode — the in-memory guards still apply */
  }
}

const doneBands = new Set<string>();
const doneShowBundles = new Set<string>();
let globalDone = false;

/** Upcoming shows, dated and sorted — the ones a travelling user will open. */
function upcomingShows(shows: ShowDTO[], limit: number): ShowDTO[] {
  const today = todayMinDate();
  return shows
    .filter((s) => s.id && s.date && String(s.date) >= today)
    .sort((a, b) => String(a.date).localeCompare(String(b.date)))
    .slice(0, limit);
}

/**
 * Cache every sub-resource a Day Sheet / show page reads. Silent: a failed
 * resource (404 on a band without that data, offline at that moment) never
 * aborts the rest of the bundle.
 */
export async function preloadShowBundle(showId: string, bandId?: string): Promise<void> {
  if (!showId) return;
  const key = `${bandId ?? ''}:${showId}`;
  if (doneShowBundles.has(key)) return;
  doneShowBundles.add(key);
  const jobs: Array<() => Promise<unknown>> = [
    () => apiGetShow(showId),
    () => apiGetShowRoadmap(showId),
    () => apiGetAccommodations(showId),
    () => apiGetRooming(showId),
    () => apiGetShowFlights(showId),
    () => apiGetShowContacts(showId),
    () => apiGetShowDistances(showId),
    () => apiGetShowGuests(showId),
    () => apiGetShowTechNotes(showId),
    () => apiGetShowMiscNotes(showId),
    () => apiGetShowCategorizedNotes(showId),
    () => apiGetRiders({ showId }),
  ];
  if (bandId) jobs.push(() => apiGetShowChecklists(bandId, showId));
  await Promise.allSettled(jobs.map((run) => run()));
}

/** Cache the Day Sheets of the closest upcoming shows (sequentially). */
export async function preloadUpcomingShows(
  shows: ShowDTO[],
  limit: number = AUTO_PRELOAD_UPCOMING_LIMIT,
): Promise<void> {
  for (const show of upcomingShows(shows, limit)) {
    try {
      await preloadShowBundle(String(show.id), show.band_id ? String(show.band_id) : undefined);
    } catch {
      /* offline / failure — keep going with the next show */
    }
  }
}

/** Fetch the shared (non-band-scoped) data into the offline cache, once. */
async function preloadGlobal(): Promise<void> {
  if (globalDone) return;
  globalDone = true;
  // Same tab refreshed within 10 min → IndexedDB already holds all of this.
  if (recentlyPreloaded(STAMP_GLOBAL)) return;
  markPreloaded(STAMP_GLOBAL);
  let shows: ShowDTO[] = [];
  try { shows = await apiGetShows(); } catch { /* cache stays as-is */ }
  const jobs: Array<() => Promise<unknown>> = [
    () => apiGetTeam(),
    // Upcoming slice — the Route map / Atlas asks for min_date=YYYY-MM-DD.
    () => apiGetShows({ minDate: todayMinDate() }),
    () => apiGetVenues(),
    () => apiGetContacts(),
    () => apiGetCalendar(),
  ];
  for (const run of jobs) {
    try { await run(); } catch { /* offline / failure — cache stays as-is */ }
  }
  await preloadUpcomingShows(shows, AUTO_PRELOAD_UPCOMING_LIMIT);
}

/** Fetch one band's data into the offline cache, once per session. */
async function preloadBand(bandId: string): Promise<void> {
  if (!bandId || doneBands.has(bandId)) return;
  doneBands.add(bandId);
  // Same tab refreshed within 10 min → this band is already cached.
  const key = stampBand(bandId);
  if (recentlyPreloaded(key)) return;
  markPreloaded(key);
  const jobs = [
    apiGetRider, apiGetConfig, apiGetMusicians, apiGetCrew,
    apiGetStagePlots, apiGetSetlists, apiGetChecklists, apiGetItinerary,
    apiGetSongs,
  ];
  for (const fn of jobs) {
    try { await fn(bandId); } catch { /* offline / failure — cache stays as-is */ }
  }
  const extras: Array<() => Promise<unknown>> = [
    // People of THIS band are what the Day Sheet / rider pages list.
    () => apiGetTeam({ band: bandId }),
    () => apiGetDaysheetLayoutEffective(bandId),
    () => apiGetPdfLayoutEffective(bandId),
  ];
  for (const run of extras) {
    try { await run(); } catch { /* offline / failure — cache stays as-is */ }
  }
}

/** Fire-and-forget preload for one band (no-op if already done). */
export function autoPreloadBand(bandId: string): void {
  if (!bandId || doneBands.has(bandId)) return;
  // Do not await — non-blocking by design.
  void preloadBand(bandId);
}

/** Fire-and-forget preload for every band the user can access + shared data. */
export function autoPreloadAll(bandIds: string[]): void {
  if (!globalDone) void preloadGlobal();
  for (const id of bandIds) autoPreloadBand(id);
}
